import { createContext, useContext } from 'react';
import { SpanContext } from '../renditions';
import { createHttpClient, HttpClient } from './http-client';
import { ChildSpan, createChildSpanFromContext, Span } from './helper';

type TelemetryCollector = {
  collect: (span: Span) => Promise<void>
}

type TelemetryContext = {
  collector: TelemetryCollector,
  createChildSpan: (name: string, child?: ChildSpan) => Span,
  rootSpanContext: SpanContext
}

/** 
 * Default context is non-functional but ensure callsites do not blow up if they require it and it has not been provided
 * Only really used by storybook, screen-entry ensures a context is always available */
export const TelemetryContext = createContext<TelemetryContext>({
  rootSpanContext: {
    traceId: 'N/A',
    spanId: 'N/A'
  },
  collector: {
    collect: async (span) => console.warn('no TelemetryContext available')
  },
  createChildSpan: () => ({} as any)
});

export function createTelemetryCollector(httpClient: HttpClient) : TelemetryCollector {
  return {
    collect: async (span: Span) : Promise<void> => {
      httpClient.POST('/trace', {
        body: [span]
      }).catch(err => console.error(err));
    }
  }
}

export function createTelemetryContext(options: {
  rootSpanContext: SpanContext,
  tenantId: string,
  baseUrl: string
}) : TelemetryContext {
  const httpClient = createHttpClient(options.baseUrl);
  const collector = createTelemetryCollector(httpClient);

  return {
    collector,
    rootSpanContext: options.rootSpanContext,
    createChildSpan: (name, span) => createChildSpanFromContext(name, {
      tenantId: options.tenantId,
      context: options.rootSpanContext
    }, span)
  };
}

export function useTelemetryContext() {
  return useContext(TelemetryContext);
}